import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiFetch } from './api';
import { API_BASE } from './apiBase';

const COMMS_API_BASE = `${API_BASE}/v1/comms`;

export type CallRecord = {
  linkedid: string;
  caller: string | null;
  caller_name?: string | null;
  time: string;
  date: string;
  outcome: string;
  direction: string;
  talk_seconds: number;
  wait_seconds: number;
  has_recording: boolean;
};

export type VoicemailRecord = {
  linkedid: string;
  caller: string | null;
  caller_name?: string | null;
  time: string;
  wait_seconds: number;
  has_recording: boolean;
};

export type CallsResponse = {
  calls: CallRecord[];
};

export type VoicemailsResponse = {
  voicemails: VoicemailRecord[];
};

export type CallsParams = {
  date_from?: string;
  date_to?: string;
  direction?: 'in' | 'out';
  outcome?: string;
  q?: string;
};

function buildQs(storeNo: number | undefined, extra: Record<string, string | undefined>): string {
  const params: string[] = [];
  if (storeNo !== undefined) params.push(`store_no=${storeNo}`);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined) params.push(`${k}=${encodeURIComponent(v)}`);
  }
  return params.length ? `?${params.join('&')}` : '';
}

export function useStoreVoicemails(storeNo?: number): UseQueryResult<VoicemailsResponse, Error> {
  return useQuery<VoicemailsResponse, Error>({
    queryKey: ['comms', 'voicemail', storeNo ?? 'own'],
    queryFn: () => apiGet<VoicemailsResponse>(`${COMMS_API_BASE}/store/voicemail${buildQs(storeNo, {})}`),
    staleTime: 30_000,
    retry: false,
  });
}

export function useStoreCalls(
  storeNo: number | undefined,
  params: CallsParams,
): UseQueryResult<CallsResponse, Error> {
  return useQuery<CallsResponse, Error>({
    queryKey: ['comms', 'calls', storeNo ?? 'own', params],
    queryFn: () =>
      apiGet<CallsResponse>(
        `${COMMS_API_BASE}/store/calls${buildQs(storeNo, {
          date_from: params.date_from,
          date_to: params.date_to,
          direction: params.direction,
          outcome: params.outcome,
          q: params.q,
        })}`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}

export async function fetchCallRecording(linkedid: string, storeNo?: number): Promise<string> {
  const qs = buildQs(storeNo, {});
  const result = await apiFetch<{ url: string }>(
    `${COMMS_API_BASE}/store/calls/${encodeURIComponent(linkedid)}/recording${qs}`,
  );
  return result.url;
}
